import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import Calendar from 'react-calendar';
import 'leaflet/dist/leaflet.css';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';
import { Grid, Typography, Button, Stack, Box, Select, MenuItem, InputLabel, FormControl, Dialog, DialogActions, DialogContent, DialogTitle, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Polyline } from "react-leaflet";
import { useMap } from 'react-leaflet';
import { useNavigate } from "react-router-dom";
import MonthlyOrderChart from "../../MonthlyOrderChart";
import StatTile from "../../StatTile";
import dayjs from "dayjs";

const toRadians = (degrees) => degrees * (Math.PI / 180);

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
    Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};


const createCustomIcon = () => {
  return L.divIcon({
    className: 'custom-icon',
    html: `
      <div style="position: relative;">
        <svg
          style="color: red; filter: drop-shadow(0 0 1px rgba(0,0,0,0.2));"
          xmlns="http://www.w3.org/2000/svg"
          height="40"
          viewBox="0 0 24 24"
          width="40"
          fill="red"
        >
          <path d="M12 2C8.13 2 5 5.13 5 9c0 3.89 3.33 7.9 6.02 11.54c.58.78 1.79.78 2.37 0C15.67 16.9 19 12.89 19 9c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = sessionStorage.getItem("selectedDate");
    return savedDate ? new Date(savedDate) : new Date();
  });
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [userLat, setUserLat] = useState('');
  const [userLng, setUserLng] = useState('');
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = JSON.parse(sessionStorage.getItem('accessToken'));
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/scrap/getallscrap`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const ordersData = response.data.data || [];
        setOrders(ordersData);
        setFilteredOrders(ordersData);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
        setFilteredOrders([]);
      }
    };
    fetchOrders();
  }, []);

  useEffect(() => {
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        console.log("Received position:", lat, lng);

        setUserLat(lat);
        setUserLng(lng);
      },
      (error) => {
        console.error("Geolocation error:", error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 5000,
      }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);


  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = JSON.parse(sessionStorage.getItem('accessToken'));
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/getusers`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const usersData = response.data.data || [];
        setTotalUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setTotalUsers([]);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const filtered = orders.filter(order =>
        new Date(order.pickupdate).toDateString() === selectedDate.toDateString()
      );
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(orders);
    }
  }, [selectedDate, orders]);

  useEffect(() => {
    sessionStorage.setItem("selectedDate", selectedDate.toISOString());
  }, [selectedDate]);

  const handleFindBestRoute = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const getOptimizedRoute = () => {
    if (!userLat || !userLng) {
      console.warn("User location not provided!");
      return;
    }

    let remainingOrders = [...filteredOrders].map(order => {
      const coords = order.address.split(',').map(coord => parseFloat(coord.trim()));
      if (isNaN(coords[0]) || isNaN(coords[1])) return null;

      return { ...order, lat: coords[0], lng: coords[1] };
    }).filter(order => order !== null);

    if (remainingOrders.length === 0) return;

    const startPoint = { lat: parseFloat(userLat), lng: parseFloat(userLng) };
    let currentLocation = startPoint;
    let optimizedRoute = [];

    while (remainingOrders.length > 0) {
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      for (let i = 0; i < remainingOrders.length; i++) {
        let distance = haversineDistance(currentLocation.lat, currentLocation.lng, remainingOrders[i].lat, remainingOrders[i].lng);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = i;
        }
      }

      optimizedRoute.push(remainingOrders[nearestIndex]);
      currentLocation = remainingOrders[nearestIndex];
      remainingOrders.splice(nearestIndex, 1);
    }

    console.log("Optimized Route:", optimizedRoute);
    setFilteredOrders(optimizedRoute);

    fetchRoute(optimizedRoute, userLat, userLng);
  };


  const handleConfirmLocation = () => {
    console.log("User's Location:", { userLat, userLng });
    setOpenDialog(false);
    getOptimizedRoute();
  };

  const fetchRoute = async (sortedOrders, userLat, userLng) => {
    if (sortedOrders.length < 2) return;

    const startCoords = [parseFloat(userLat), parseFloat(userLng)];
    if (isNaN(startCoords[0]) || isNaN(startCoords[1])) {
      console.error("Invalid user coordinates:", startCoords);
      return;
    }

    const orderCoords = sortedOrders
      .map(order => {
        const coords = order.address.split(',').map(coord => parseFloat(coord.trim()));
        return isNaN(coords[0]) || isNaN(coords[1]) ? null : coords;
      })
      .filter(coords => coords !== null);

    if (orderCoords.length < 1) return;

    const coordinates = [startCoords, ...orderCoords];

    const coordString = coordinates.map(coord => coord.reverse().join(',')).join(';');

    console.log("what i am sending", coordString);
    const url = `https://router.project-osrm.org/route/v1/driving/${coordString}?overview=full&geometries=geojson`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.routes && data.routes.length > 0) {
        const routePath = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);

        setRouteCoordinates(routePath);
      }
    } catch (error) {
      console.error("Error fetching route:", error);
    }
  };

  const MapComponent = ({ userLat, userLng }) => {
    const map = useMap();

    useEffect(() => {
      if (map && userLat && userLng) {
        const circle = L.circle([userLat, userLng], {
          color: "blue",
          fillColor: "lightblue",
          fillOpacity: 0.5,
          radius: 50,
        })
          .addTo(map)
          .bindPopup("User's Location");

        return () => {
          map.removeLayer(circle);
        };
      }
    }, [map, userLat, userLng]);

    return null;
  };


  const handleClear = () => {
    setSelectedYear('');
    setSelectedMonth('');
  };

  const filteredOrdersGraph = orders.filter(order => {
    const orderDate = dayjs(order.pickupdate);
    const matchesYear = selectedYear ? orderDate.year().toString() === selectedYear : true;
    const matchesMonth = selectedMonth ? orderDate.format('MM') === selectedMonth : true;
    return matchesYear && matchesMonth;
  });

  const years = Array.from(new Set(orders.map(order => dayjs(order.pickupdate).year().toString())));
  const months = [...Array(12)].map((_, i) => ({
    value: (i + 1).toString().padStart(2, '0'),
    label: dayjs().month(i).format('MMMM'),
  }));

  return (
    <Grid container spacing={2} sx={{ padding: '20px' }}>
      <Grid item xs={12} md={8}>
        <Typography variant="h4" gutterBottom>Orders Map</Typography>
        <Box sx={{ height: '60vh', width: '100%' }}>
          <MapContainer center={[25.5779, 91.8837]} zoom={15} maxZoom={18} minZoom={2} style={{ height: '100%' }}>
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              attribution="Google Maps"
            />

            <MapComponent userLat={userLat} userLng={userLng} />

            {filteredOrders.map((order, index) => {
              const coords = order.address.split(',').map(coord => parseFloat(coord.trim()));

              if (isNaN(coords[0]) || isNaN(coords[1])) {
                console.warn('Invalid coordinates for order:', order);
                return null;
              }

              return (
                <Marker
                  key={order._id}
                  position={coords}
                  icon={createCustomIcon()}
                >
                  <Popup>
                    <div style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/orderDetails/${order._id}`)}>
                      <Typography variant="subtitle1">Stop #{index + 1}</Typography>
                      <Typography variant="body2">
                        Order #{order._id.slice(-4)} <br />
                        Name: {order.user.username} <br />
                        Address: {order.address} <br />
                        Distance: {order.distance?.toFixed(2)} km
                      </Typography>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {routeCoordinates.length > 0 && (
              <Polyline
                positions={routeCoordinates}
                color="blue"
                weight={6}
                opacity={0.7}
              />
            )}
          </MapContainer>
          <Stack direction="row" spacing={2} style={{ marginTop: '10px' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => { setRouteCoordinates([]); }}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleFindBestRoute}
            >
              Find Best Route
            </Button>
          </Stack>
        </Box>

        {isMobile && (
          <Box
            sx={{
              marginTop: 7,
              padding: 2,
              border: '1px solid #ccc',
              borderRadius: '8px',
              textAlign: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setCalendarDialogOpen(true)}
          >
            <Typography variant="body1">Selected Date: {selectedDate.toDateString()}</Typography>
          </Box>
        )}

        {isMobile ? (
          <>
            <Box sx={{ my: 7 }}>
              <Typography variant="h6" gutterBottom>Monthly Orders Overview</Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="year-label">Year</InputLabel>
                  <Select
                    labelId="year-label"
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <MenuItem value="">All Years</MenuItem>
                    {years.map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="month-label">Month</InputLabel>
                  <Select
                    labelId="month-label"
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    <MenuItem value="">All Months</MenuItem>
                    {months.map(month => (
                      <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button variant="outlined" color="secondary" onClick={handleClear}>
                  Clear Filter
                </Button>
              </Stack>

              <MonthlyOrderChart orders={filteredOrdersGraph} />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatTile
                  label="TOTAL ORDERS"
                  value={orders.length}
                  gradient="linear-gradient(to right, #232526, #414345)"
                />
              </Grid>
              <Grid item xs={6}>
                <StatTile
                  label="TOTAL USERS"
                  value={totalUsers.length}
                  gradient="linear-gradient(to right, #f953c6, #b91d73)"
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <Grid container spacing={2} sx={{ my: 7 }}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>Monthly Orders Overview</Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="year-label">Year</InputLabel>
                  <Select
                    labelId="year-label"
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <MenuItem value="">All Years</MenuItem>
                    {years.map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="month-label">Month</InputLabel>
                  <Select
                    labelId="month-label"
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    <MenuItem value="">All Months</MenuItem>
                    {months.map(month => (
                      <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button variant="outlined" color="secondary" onClick={handleClear}>
                  Clear Filter
                </Button>
              </Stack>

              <MonthlyOrderChart orders={filteredOrdersGraph} />
            </Grid>

            <Grid item xs={12} md={4} sx={{ mt: { xs: 2, md: 6 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4} sx={{ ml: 40 }}>
                  <StatTile
                    label="TOTAL ORDERS"
                    value={orders.length}
                    gradient="linear-gradient(to right, #232526, #414345)"
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ ml: 40 }}>
                  <StatTile
                    label="TOTAL USERS"
                    value={totalUsers.length}
                    gradient="linear-gradient(to right, #f953c6, #b91d73)"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      {
        !isMobile && (
          <Grid item xs={12} md={4}>
            <Typography variant="h5" gutterBottom>Select Date</Typography>
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              className="react-calendar"
            />
          </Grid>
        )
      }

      <Dialog open={calendarDialogOpen} onClose={() => setCalendarDialogOpen(false)}>
        <DialogTitle>Select a Date</DialogTitle>
        <DialogContent>
          <Calendar
            onChange={(date) => {
              setSelectedDate(date);
              setCalendarDialogOpen(false);
            }}
            value={selectedDate}
            className="react-calendar"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid >
  );
};

export default Dashboard;
